import { BannerContent } from '@/components/card/ibk/IbkBanner';
import { formatCurrency, formatMessage } from '@/utils/translationHelper';
import { type ReactNode } from 'react';
import type { Locale } from '@/i18n';
import type { InstantBookableVenue, IbkStrings } from '@/components/card/ibk/IbkBanner';
import { Currency } from '@/models/Currency';

export type AvailabilityProps = {
  instantBookableVenue: InstantBookableVenue;
  meetingRoomCost?: number;
  guestRoomCost?: number;
  currencyCode?: string;
  eventDatesAvailable: boolean;
  guestRoomDatesAvailable: boolean;
  preFilledMeetingSpaceAttendees?: number;
  preFilledGuestRoomCount?: number;
  strings: IbkStrings;
  locale: Locale;
};

export function getAvailabilityOutput({
  instantBookableVenue,
  meetingRoomCost,
  guestRoomCost,
  currencyCode,
  eventDatesAvailable,
  guestRoomDatesAvailable,
  preFilledMeetingSpaceAttendees,
  preFilledGuestRoomCount,
  strings,
  locale
}: AvailabilityProps): BannerContent {
  const { meetingSpaceIntegration, sleepingSpaceIntegration } = instantBookableVenue;
  const formattedMeetingRoomRate =
    meetingRoomCost && formatCurrency(locale, meetingRoomCost, currencyCode as Currency, 0, 0);
  const formattedGuestRoomRate = guestRoomCost && formatCurrency(locale, guestRoomCost, currencyCode as Currency, 0, 0);
  let guestRoomRateText: string = '';
  let meetingRoomRateText: string = '';
  let isMeetingRoomPriority: boolean = false;
  let primaryRoomAvailability: string;
  let secondaryRoomAvailability: string;
  let isBookNowAvailable = false;
  if (!eventDatesAvailable && !guestRoomDatesAvailable) {
    if (meetingSpaceIntegration) {
      meetingRoomRateText = formatMessage(strings.csn_ibk_only_meetingRooms, locale);
    }
    if (sleepingSpaceIntegration) {
      guestRoomRateText = formatMessage(strings.csn_ibk_only_guestRooms, locale);
    }
    primaryRoomAvailability = guestRoomRateText;
    secondaryRoomAvailability = meetingRoomRateText;
  } else {
    if (meetingSpaceIntegration && !sleepingSpaceIntegration && meetingRoomCost) {
      guestRoomRateText = formatMessage(strings.csn_ibk_guestRooms_not_bookable, locale);
      meetingRoomRateText = formatMessage(strings.csn_ibk_booking_meetingRoomRate, locale, {
        b: (chunks: ReactNode[]) => `<strong>${chunks}</strong>`,
        rate: formattedMeetingRoomRate
      });
      if (eventDatesAvailable && preFilledMeetingSpaceAttendees) {
        isBookNowAvailable = true;
      }
    }
    if (sleepingSpaceIntegration && !meetingSpaceIntegration && guestRoomCost) {
      guestRoomRateText = formatMessage(strings.csn_ibk_booking_guestRoomRate, locale, {
        b: (chunks: ReactNode[]) => `<strong>${chunks}</strong>`,
        rate: formattedGuestRoomRate
      });
      meetingRoomRateText = formatMessage(strings.csn_ibk_meetingRooms_not_bookable, locale);
      if (preFilledGuestRoomCount) {
        isBookNowAvailable = true;
      }
    }
    if (meetingSpaceIntegration && meetingRoomCost && sleepingSpaceIntegration && !guestRoomCost) {
      guestRoomRateText = formatMessage(strings.csn_ibk_guestRooms_not_available, locale);
      meetingRoomRateText = formatMessage(strings.csn_ibk_booking_meetingRoomRate, locale, {
        b: (chunks: ReactNode[]) => `<strong>${chunks}</strong>`,
        rate: formattedMeetingRoomRate
      });
      isMeetingRoomPriority = true;
      if (eventDatesAvailable && preFilledMeetingSpaceAttendees) {
        isBookNowAvailable = true;
      }
    }
    if (sleepingSpaceIntegration && guestRoomCost && meetingSpaceIntegration && !meetingRoomCost) {
      guestRoomRateText = formatMessage(strings.csn_ibk_booking_guestRoomRate, locale, {
        b: (chunks: ReactNode[]) => `<strong>${chunks}</strong>`,
        rate: formattedGuestRoomRate
      });
      meetingRoomRateText = formatMessage(strings.csn_ibk_meetingRooms_not_available, locale);
      if (preFilledGuestRoomCount) {
        isBookNowAvailable = true;
      }
    }
    if (sleepingSpaceIntegration && guestRoomCost && meetingSpaceIntegration && meetingRoomCost) {
      guestRoomRateText = formatMessage(strings.csn_ibk_booking_guestRoomRate, locale, {
        b: (chunks: ReactNode[]) => `<strong>${chunks}</strong>`,
        rate: formattedGuestRoomRate
      });
      meetingRoomRateText = formatMessage(strings.csn_ibk_booking_meetingRoomRate, locale, {
        b: (chunks: ReactNode[]) => `<strong>${chunks}</strong>`,
        rate: formattedMeetingRoomRate
      });
      if (preFilledMeetingSpaceAttendees && !preFilledGuestRoomCount) {
        isMeetingRoomPriority = true;
      }
      if (
        (eventDatesAvailable && preFilledMeetingSpaceAttendees) ||
        (eventDatesAvailable && preFilledGuestRoomCount && !guestRoomDatesAvailable) ||
        (!eventDatesAvailable && !preFilledMeetingSpaceAttendees && preFilledGuestRoomCount && guestRoomDatesAvailable)
      ) {
        isBookNowAvailable = true;
      }
    }
    primaryRoomAvailability = isMeetingRoomPriority ? meetingRoomRateText : guestRoomRateText;
    secondaryRoomAvailability = !isMeetingRoomPriority ? meetingRoomRateText : guestRoomRateText;
  }

  return {
    primaryRoomAvailability,
    secondaryRoomAvailability,
    isBookNowAvailable
  };
}
